/**
 * Mock data for storefront pages — used when the API is unavailable (no backend).
 * All amounts are in FCFA. All text visible to user is in French.
 */

// ─── CMS ─────────────────────────────────────────────────────────────
const mockHeroSlides = {
  slides: [
    { id: "hs1", title: "Countre Dairy", title_highlight: "Qualité Premium", badge: "Nouveau", subtitle: "Découvrez notre gamme de produits laitiers frais", image_url: "/placeholder-hero-1.jpg", cta_text: "Voir les produits", cta_link: "/#catalog", sort_order: 1 },
    { id: "hs2", title: "Yousra", title_highlight: "Saveurs d'Afrique", badge: null, subtitle: "Riz parfumé et mayonnaise de qualité supérieure", image_url: "/placeholder-hero-2.jpg", cta_text: "Commander", cta_link: "/#catalog", sort_order: 2 },
  ],
};

const mockBrandShowcases = {
  brands: [
    { id: "bs1", brand_name: "Countre Dairy", slug: "countre-dairy", tagline: "La fraîcheur au quotidien", description: "Produits laitiers frais", logo_url: null, cover_image_url: null, accent_color: "#D4A574", sort_order: 1 },
    { id: "bs2", brand_name: "Yousra", slug: "yousra", tagline: "Le goût de l'excellence", description: "Mayonnaise et riz premium", logo_url: null, cover_image_url: null, accent_color: "#C68B59", sort_order: 2 },
    { id: "bs3", brand_name: "Florida", slug: "florida", tagline: "Conserves de confiance", description: "Conserves alimentaires", logo_url: null, cover_image_url: null, accent_color: "#8B6914", sort_order: 3 },
    { id: "bs4", brand_name: "KamaX / KamiX", slug: "kamax-kamix", tagline: "Propreté garantie", description: "Produits d'entretien", logo_url: null, cover_image_url: null, accent_color: "#2E5D3A", sort_order: 4 },
  ],
};

const mockDeliveryZones = [
  { id: "z1", zone_name: "Dakar Centre", fee_own_car_fcfa: 1500, fee_own_bike_fcfa: 1000, fee_yango_estimated_fcfa: 2000, estimated_time_minutes: 30, is_serviceable: true },
  { id: "z2", zone_name: "Parcelles Assainies", fee_own_car_fcfa: 2000, fee_own_bike_fcfa: 1500, fee_yango_estimated_fcfa: 2500, estimated_time_minutes: 45, is_serviceable: true },
  { id: "z3", zone_name: "Hann Mariste", fee_own_car_fcfa: 1000, fee_own_bike_fcfa: 500, fee_yango_estimated_fcfa: 1500, estimated_time_minutes: 15, is_serviceable: true },
  { id: "z4", zone_name: "Guédiawaye", fee_own_car_fcfa: 3000, fee_own_bike_fcfa: 2000, fee_yango_estimated_fcfa: 3500, estimated_time_minutes: 60, is_serviceable: true },
  { id: "z5", zone_name: "Rufisque", fee_own_car_fcfa: 4000, fee_own_bike_fcfa: 3000, fee_yango_estimated_fcfa: 5000, estimated_time_minutes: 90, is_serviceable: false },
];

// ─── Products (fallback when API is unavailable) ────────────────────
const mockProducts = {
  products: [
    { id: "mp1", name: "Lait Frais Countre Dairy 1L", brand: "countre_dairy", category: "dairy", price_fcfa: 1500, unit: "carton", stock: 120, min_order_qty: 1, image_url: null },
    { id: "mp2", name: "Yaourt Nature Countre Dairy", brand: "countre_dairy", category: "dairy", price_fcfa: 800, unit: "pack", stock: 85, min_order_qty: 1, image_url: null },
    { id: "mp3", name: "Mayonnaise Yousra 500ml", brand: "yousra", category: "sauce", price_fcfa: 1200, unit: "carton", stock: 200, min_order_qty: 1, image_url: null },
    { id: "mp4", name: "Riz Parfumé Yousra 5kg", brand: "yousra", category: "rice", price_fcfa: 4500, unit: "sac", stock: 150, min_order_qty: 1, image_url: null },
    { id: "mp5", name: "Conserve Viande Florida 400g", brand: "florida", category: "food", price_fcfa: 2000, unit: "carton", stock: 90, min_order_qty: 1, image_url: null },
    { id: "mp6", name: "Conserve Tomate Florida 800g", brand: "florida", category: "food", price_fcfa: 1800, unit: "carton", stock: 110, min_order_qty: 1, image_url: null },
    { id: "mp7", name: "Détergent KamaX 1kg", brand: "kamax_kamix", category: "cleaning", price_fcfa: 2500, unit: "carton", stock: 75, min_order_qty: 1, image_url: null },
    { id: "mp8", name: "Savon KamiX 250g", brand: "kamax_kamix", category: "cleaning", price_fcfa: 600, unit: "pack", stock: 300, min_order_qty: 1, image_url: null },
    { id: "mp9", name: "Crème Fraîche Countre Dairy", brand: "countre_dairy", category: "dairy", price_fcfa: 1100, unit: "pack", stock: 60, min_order_qty: 1, image_url: null },
  ],
  pagination: { page: 1, limit: 20, total: 9 },
};

// ─── Route → Mock data mapping ───────────────────────────────────────
const mockRoutes: Record<string, unknown> = {
  "/api/cms/hero-slides": mockHeroSlides,
  "/api/cms/brand-showcases": mockBrandShowcases,
  "/api/delivery/zones": mockDeliveryZones,
  "/api/catalog/products": mockProducts,
};

/**
 * Look up mock data for a given storefront API path.
 * Supports query-string filtering for catalog products.
 */
export function getMockData(path: string): unknown | undefined {
  const [clean, qs] = path.split("?");
  const route = clean.replace(/\/+$/, "");

  // Handle product detail pages: /api/catalog/products/:id
  const productDetailMatch = route.match(/^\/api\/catalog\/products\/(.+)$/);
  if (productDetailMatch) {
    const productId = productDetailMatch[1];
    const product = mockProducts.products.find((p) => p.id === productId);
    return product ?? undefined;
  }

  const data = mockRoutes[route];
  if (data === undefined) return undefined;

  // Apply client-side filtering for catalog products mock
  if (route === "/api/catalog/products" && qs) {
    const params = new URLSearchParams(qs);
    const brand = params.get("brand");
    const category = params.get("category");
    const allProducts = (data as typeof mockProducts).products;
    const filtered = allProducts.filter((p) => {
      if (brand && p.brand !== brand) return false;
      if (category && p.category !== category) return false;
      return true;
    });
    return { products: filtered, pagination: { page: 1, limit: 20, total: filtered.length } };
  }

  return data;
}
